/**
 * v3.deathNotice — Death Notice rebuild Wave 1 client (locked 2026-04-27
 * "go all defaults"). Backed by Functions/DeathNoticeFunction.cs:
 *
 *   GET  /v3/death-notice/cases?fsr=&includeClosed=
 *   GET  /v3/death-notice/cases/{caseId}
 *   POST /v3/death-notice/cases                  (intake)
 *   POST /v3/death-notice/cases/{caseId}/progress
 *
 * Pilot-gated end-to-end. An intake that is gated returns HTTP 201 with
 * `result.pilotGated: true` so the caller renders the legacy fallback
 * path without losing the captured intake fields.
 */

import type {
  RequestOptions,
  DeathNoticeCaseDetail,
  DeathNoticeCaseListRow,
  DeathNoticeCadenceSummary,
  DeathNoticeIntakeRequest,
  DeathNoticeIntakeResult,
  DeathNoticeProgressRequest,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export interface DeathNoticeListResponse {
  success: boolean;
  count?: number;
  cases?: ReadonlyArray<DeathNoticeCaseListRow>;
  error?: string;
}

export interface DeathNoticeGetResponse {
  success: boolean;
  case?: DeathNoticeCaseDetail['case'];
  cadence?: ReadonlyArray<DeathNoticeCadenceSummary>;
  error?: string;
}

export interface DeathNoticeIntakeResponse {
  success: boolean;
  result?: DeathNoticeIntakeResult;
  error?: string;
}

export interface DeathNoticeProgressResponse {
  success: boolean;
  cadenceTask?: DeathNoticeCadenceSummary;
  error?: string;
}

export interface DeathNoticeListFilters {
  fsr?: string;
  includeClosed?: boolean;
}

export class DeathNoticeApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    filters: DeathNoticeListFilters = {},
    opts?: RequestOptions,
  ): Promise<DeathNoticeListResponse> {
    return this.http.get<DeathNoticeListResponse>('/v3/death-notice/cases', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public get(caseId: number, opts?: RequestOptions): Promise<DeathNoticeGetResponse> {
    return this.http.get<DeathNoticeGetResponse>(
      `/v3/death-notice/cases/${encodeURIComponent(String(caseId))}`,
      { ...opts },
    );
  }

  public intake(
    bypassKey: string,
    body: DeathNoticeIntakeRequest,
    opts?: RequestOptions,
  ): Promise<DeathNoticeIntakeResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<DeathNoticeIntakeResponse>('/v3/death-notice/cases', {
      ...opts,
      headers,
      body,
    });
  }

  public progress(
    bypassKey: string,
    caseId: number,
    body: DeathNoticeProgressRequest,
    opts?: RequestOptions,
  ): Promise<DeathNoticeProgressResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<DeathNoticeProgressResponse>(
      `/v3/death-notice/cases/${encodeURIComponent(String(caseId))}/progress`,
      { ...opts, headers, body },
    );
  }
}
